// Section types admins can add from the editor.
import { Col, Row } from 'react-bootstrap';
import { FaExchangeAlt, FaPen } from 'react-icons/fa';
import { LinkButton, Section } from '../content/layout';
import { FieldSpec, useImageSrc, useSite } from '../content/context';
import { ActionButton, AddTile, ChangePhotoButton, EditableText, ItemControls } from '../edit/primitives';
import { moveItem, removeItem, replaceItem } from '../edit/list';
import Gallery from '../Gallery';
import VideoPlayer from '../VideoPlayer';
import { safeHttpUrl, toBandcampEmbedUrl, toYouTubeEmbedUrl } from '../urls';

interface Props {
  section: Section;
  update: (patch: Partial<Section>) => void;
}

/** Paragraphs separated by blank lines; editable in place in the editor. */
export function Paragraphs({ text, onChange, className }: { text?: string; onChange: (text: string) => void; className?: string }) {
  const { edit } = useSite();
  if (edit) return <EditableText as="div" multiline label="Section text" value={text} onChange={onChange} className={className} />;
  const paragraphs = (text ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className={className}>
      {paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

export function TextSection({ section, update }: Props) {
  return <Paragraphs className="af-section-text" text={section.text} onChange={(text) => update({ text })} />;
}

export function PhotoTextSection({ section, update }: Props) {
  const { edit } = useSite();
  const imageSrc = useImageSrc();
  const src = imageSrc(section.image);
  const photoFirst = section.imageSide !== 'right';
  const photo = (
    <Col md={6} className="mb-3 af-frame">
      {src ? <img src={src} alt={section.title || 'Arc Flash'} /> : edit ? <div className="af-placeholder">No photo yet</div> : null}
      <ChangePhotoButton label={src ? 'Change photo' : 'Add photo'} onChange={(image) => update({ image })} />
    </Col>
  );
  const text = (
    <Col md={6} className="mb-3 d-flex flex-column justify-content-center">
      <Paragraphs className="af-section-text mb-0" text={section.text} onChange={(t) => update({ text: t })} />
    </Col>
  );
  return (
    <div className="container af-photo-text">
      {edit ? (
        <div className="d-flex justify-content-center mb-2">
          <ActionButton label="Swap photo and text sides" icon={<FaExchangeAlt />} showLabel onClick={() => update({ imageSide: photoFirst ? 'right' : 'left' })} />
        </div>
      ) : null}
      <Row className="align-items-center">
        {photoFirst ? photo : text}
        {photoFirst ? text : photo}
      </Row>
    </div>
  );
}

const videoField: FieldSpec = {
  name: 'url',
  label: 'YouTube link or Bandcamp player link',
  type: 'url',
  required: true,
  hint: 'Any YouTube link, or the player address (src="…") from Bandcamp’s Share / Embed code.',
  validate: (v) => (toYouTubeEmbedUrl(v) || toBandcampEmbedUrl(v) ? undefined : "That isn't a YouTube or Bandcamp player link."),
};

export function VideoSection({ section, update }: Props) {
  const { edit } = useSite();
  const youtube = toYouTubeEmbedUrl(section.url);
  const bandcamp = youtube ? undefined : toBandcampEmbedUrl(section.url);
  const editVideo = async () => {
    if (!edit) return;
    const result = await edit.openForm({ title: 'Video', fields: [videoField], values: { url: section.url } });
    if (result && 'values' in result) update({ url: result.values.url.trim() });
  };
  if (!youtube && !bandcamp && !edit) return null;
  return (
    <div className="af-video af-embed px-2">
      {youtube ? <VideoPlayer url={youtube} title={section.title || 'Video'} /> : null}
      {bandcamp ? <iframe width="100%" height="315" src={bandcamp} title={section.title || 'Bandcamp'} allowFullScreen /> : null}
      {!youtube && !bandcamp ? <div className="af-placeholder">No video yet. Use the pencil to add a YouTube or Bandcamp link.</div> : null}
      {edit ? (
        <div className="af-actions">
          <ActionButton label="Edit video link" icon={<FaPen />} showLabel onClick={editVideo} />
        </div>
      ) : null}
    </div>
  );
}

export function PhotosSection({ section, update }: Props) {
  return (
    <div className="px-2">
      <Gallery photos={section.photos ?? []} onChange={(photos) => update({ photos })} />
    </div>
  );
}

const buttonFields: FieldSpec[] = [
  { name: 'label', label: 'Button text', required: true, placeholder: 'Buy merch' },
  {
    name: 'url',
    label: 'Link',
    type: 'url',
    required: true,
    placeholder: 'https://',
    validate: (v) => (safeHttpUrl(v) ? undefined : 'Enter a full link starting with https://'),
  },
];

export function ButtonsSection({ section, update }: Props) {
  const { edit } = useSite();
  const buttons = section.buttons ?? [];
  const save = (next: LinkButton[]) => update({ buttons: next });

  const openButton = async (index?: number) => {
    if (!edit) return;
    const current = index === undefined ? undefined : buttons[index];
    const result = await edit.openForm({
      title: current ? 'Edit button' : 'Add a button',
      fields: buttonFields,
      values: { ...current },
      submitLabel: current ? 'Save' : 'Add button',
    });
    if (!result || !('values' in result)) return;
    const button = { label: result.values.label.trim(), url: result.values.url.trim() };
    save(index === undefined ? [...buttons, button] : replaceItem(buttons, index, button));
  };

  return (
    <div className="af-buttons px-2">
      {buttons.map((button, index) => {
        const href = safeHttpUrl(button.url);
        if (!edit) {
          return href ? (
            <a key={index} className="btn btn-lg af-button" href={href} target="_blank" rel="noopener noreferrer">
              {button.label}
            </a>
          ) : null;
        }
        return (
          <div key={index} className="af-button-wrap">
            <span className="btn btn-lg af-button">{button.label}</span>
            <ItemControls
              noun="button"
              index={index}
              count={buttons.length}
              onEdit={() => openButton(index)}
              onMove={(from, to) => save(moveItem(buttons, from, to))}
              onRemove={() => save(removeItem(buttons, index))}
            />
          </div>
        );
      })}
      {edit ? (
        <div style={{ width: 200 }}>
          <AddTile label="Add a button" onClick={() => openButton()} />
        </div>
      ) : null}
    </div>
  );
}
